import { useCallback, useEffect, useState } from "react";
import {
  ART_KINDS,
  ART_PROMPT_MAX,
  artPromptParts,
  randomArtIdea,
  ART_REFS_MAX,
  type ArtKind,
} from "@greg-chick/shared";
import { generateArt, getSiteText } from "../lib/contentApi";
import { saveBlob } from "../lib/download";
import { ActionButton } from "./ActionButton";

/**
 * 🎨 **AI로 그려보기** — 무엇을 그릴지 한 줄 적으면 후보를 보여 주고, 고른 것을 그 자리에 넣는다.
 *
 * ## 두 갈래를 한 자리에
 * ① **여기서 바로 만들기** — OpenAI 열쇠로 그린다(읽어주기와 같은 열쇠). 자동이지만 장당 돈이 든다.
 * ② **쓰시는 AI에서 만들어 붙여넣기** — ChatGPT·제미나이·그록 **구독**으로 직접 그린 뒤
 *    Ctrl+V. 구독료 안에서 쓰니 추가 비용이 없고, 우리가 안 붙인 서비스도 다 쓸 수 있다.
 *    ②를 쓰기 쉽게 **프롬프트를 복사**해 주는 버튼을 같이 둔다 — 그게 손이 가장 많이 가는 일이다.
 *
 * ## 초안이지 정답이 아니다
 * 고른 그림은 **[파일 고르기]와 같은 길**로 들어간다 — 규격 검사·배경 지우기·자동 줄이기가
 * 전부 그 길에 이미 있다. 여기서 따로 저장하지 않는다(길이 둘이면 조용히 갈린다).
 *
 * ## 🔴 그림체는 한 번에 안 맞는다
 * 이 책은 연필 선 + 수채다. AI 는 기본적으로 반들반들한 그림을 그린다. 그래서 후보를
 * 여러 장 뽑아 고르는 걸 전제로 만든다 — 「한 번 눌러 끝」이라고 말하지 않는다.
 */
export function ArtDraftBox({
  kind,
  name,
  onPick,
  refs = [],
  preset,
}: {
  kind: ArtKind;
  /** 운영자가 지은 이름 — 첫 설명으로 채워 준다(빈 칸이 가장 흔한 벽이다). */
  name?: string;
  /** 고른 그림. **파일로 준다** — [파일 고르기]와 같은 길로 들어가야 검사가 붙는다. */
  onPick: (file: File) => void;
  /**
   * 🖼️ **「이 그림처럼」에 쓸 후보들.** 같은 종류로 이미 만들어 둔 것들을 부르는 쪽이 준다.
   * 말로 아무리 적어도 모델은 자기 취향으로 그린다 — 그림을 보여 주는 편이 훨씬 잘 맞는다.
   */
  refs?: { key: string; url: string; label: string }[];
  /**
   * 📌 **그릴 것이 이미 정해진 칸**(옆모습·뒷모습 같은 것).
   *
   * 이런 칸에서는 「무엇을 그릴까요?」를 열어 두면 안 된다 — 운영자가 거기에 뭘 적으면
   * 구도 문장과 부딪혀 엉뚱한 그림이 나온다. 대신 **무엇을 그리는지 한 줄로 알려 주고**,
   * 고칠 사람은 [📝 보내는 글]에서 고친다(거기서는 여전히 다 고칠 수 있다).
   *
   * `refs` 는 미리 켜 둘 참고 그림 키다 — 「같은 아이」를 그리려면 그 아이를 **꼭** 보여
   * 줘야 해서, 여기서는 운영자가 켜는 게 아니라 기본으로 켜져 있다.
   */
  preset?: {
    subject: string;
    /**
     * 보낼 글을 만드는 함수. **문자열이 아니라 함수인 이유**: 운영자가 정한 그림체는
     * 이 안에서 읽어 오므로(`getSiteText`), 부르는 쪽은 그 값을 모른다. 값을 미리 만들어
     * 넘기면 운영자가 고쳐 둔 결이 이 칸에서만 조용히 무시된다.
     */
    editable: (style?: string) => string;
    refs?: string[];
  };
}) {
  const spec = ART_KINDS[kind];
  const [what, setWhat] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [left, setLeft] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  /**
   * 🎨 운영자가 정한 그림체. 비어 있으면 코드의 기본 결을 쓴다(엣지가 그렇게 판단한다).
   * 열었을 때만 읽는다 — 안 쓰는 화면에서 문구를 부르지 않게.
   */
  const [style, setStyle] = useState("");
  /**
   * 고른 참고 그림(키). 기본은 아무것도 안 고른 상태 — 돈이 더 드는 길이라 운영자가 켠다.
   * 다만 **정해진 칸**(`preset`)은 참고 그림 없이는 뜻이 없어서 미리 켜 둔다.
   */
  const [picked, setPicked] = useState<string[]>(preset?.refs ?? []);
  useEffect(() => {
    if (!open || style) return;
    void getSiteText()
      .then((t) => setStyle(t.artStyle ?? ""))
      .catch(() => undefined);
  }, [open, style]);

  const said = preset ? preset.subject : what.trim() || name?.trim() || "";

  /**
   * 📝 **AI 에게 실제로 보내는 글.** 안 보여 주면 그림이 왜 그렇게 나왔는지 알 수가 없고,
   * 못 고치면 「모자 챙을 더 넓게」 같은 미세 조정을 할 길이 없다.
   *
   * 🔴 다만 **뒷부분(아이 안전·투명 배경)은 못 고친다.** 실수로 지우면 모자에 흰 판이
   * 붙어 오는데, 운영자는 이유를 알 길이 없다. 화면에도 그렇게 나눠 보여 준다 —
   * 보이는 것과 보내는 것이 갈리지 않게.
   */
  const base = artPromptParts(kind, said, style || undefined);
  // 정해진 칸은 앞부분을 통째로 갈아 끼운다 — 뒷부분(아이 안전·투명)은 어느 쪽이든 그대로 붙는다.
  const parts = preset ? { editable: preset.editable(style || undefined), fixed: base.fixed } : base;
  const [edited, setEdited] = useState<string | null>(null);
  const shown = edited ?? parts.editable;
  const touched = edited !== null && edited.trim() !== parts.editable.trim();

  /**
   * 참고 그림을 **바이트로** 만들어 보낸다. 주소를 보내면 엣지가 그 주소로 요청하게 되고,
   * 그건 밖에서 준 주소로 우리 서버가 아무 데나 나가는 길이 된다(불변식 7).
   */
  const refDataUrls = useCallback(async () => {
    const out: string[] = [];
    for (const r of refs.filter((x) => picked.includes(x.key)).slice(0, ART_REFS_MAX)) {
      try {
        const blob = await (await fetch(r.url, { cache: "force-cache" })).blob();
        out.push(
          await new Promise<string>((res, rej) => {
            const fr = new FileReader();
            fr.onload = () => res(String(fr.result));
            fr.onerror = () => rej(new Error("참고 그림을 못 읽었어요"));
            fr.readAsDataURL(blob);
          }),
        );
      } catch {
        // 한 장을 못 읽어도 나머지로 진행한다 — 참고 그림은 있으면 좋은 것이지 필수가 아니다
      }
    }
    return out;
  }, [refs, picked]);

  const make = useCallback(async () => {
    const got = await generateArt(kind, said, {
      n: 2,
      style: style || undefined,
      prompt: touched ? shown : undefined,
      refs: picked.length > 0 ? await refDataUrls() : undefined,
    });
    setImages(got.images);
    setLeft(got.left);
  }, [kind, said, style, touched, shown, picked, refDataUrls]);

  /** data: 주소를 파일로 — SlotUploader 가 받는 모양(그 길에 검사가 다 붙어 있다). */
  const toFile = useCallback(
    async (dataUrl: string, i: number) => {
      const blob = await (await fetch(dataUrl)).blob();
      const ext = blob.type.includes("webp") ? "webp" : "png";
      // 이름을 사람 말로 — 내려받아 폴더에서 볼 때 무엇인지 알아야 한다
      const base = (name?.trim() || spec.label).replace(/[\\/:*?"<>|]/g, "");
      return new File([blob], `${base}_${i + 1}.${ext}`, { type: blob.type });
    },
    [name, spec.label],
  );

  /** ⬇ 내 컴퓨터로 — 굿즈·인쇄에 쓰거나, 다른 도구로 다듬을 때. */
  const download = useCallback(
    async (dataUrl: string, i: number) => {
      const f = await toFile(dataUrl, i);
      saveBlob(f, f.name);
    },
    [toFile],
  );

  const [over, setOver] = useState(false);

  /**
   * 🖼️ **바깥에서 만든 그림 받기** — 파일 고르기 · 끌어다 놓기 · 붙여넣기 셋 다 여기로 온다.
   *
   * 길이 셋이지만 **받는 곳은 하나**다. 각자 따로 처리하면 한 길에만 검사가 빠진다.
   */
  const takeFile = useCallback(
    (f: File | null | undefined) => {
      if (!f) return;
      if (!f.type.startsWith("image/")) {
        setNote(`그림 파일이 아니에요(${f.type || f.name}). png·webp·jpg 를 넣어 주세요.`);
        return;
      }
      setNote(null);
      onPick(f);
    },
    [onPick],
  );

  /** 붙여넣기(Ctrl+V) — 파일로 저장하지 않고 바로 넣고 싶을 때. 곁들이는 길이다. */
  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const item = [...e.clipboardData.items].find((x) => x.type.startsWith("image/"));
      if (!item) return; // 글자를 붙여넣은 것뿐일 수 있다 — 나무라지 않는다
      e.preventDefault();
      takeFile(item.getAsFile());
    },
    [takeFile],
  );

  return (
    <details className="artd" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="artd__sum">🤖 AI로 그려보기{preset ? ` — ${preset.subject}` : "(선택)"}</summary>

      {preset ? (
        <p className="artd__what artd__what--fixed">
          그릴 것: <b>{preset.subject}</b>
          <small>서 있는 모습을 참고로 보내요 — 같은 아이가 나오게 하려면 꼭 필요해요</small>
        </p>
      ) : (
      <label className="artd__what">
        무엇을 그릴까요?
        <span className="artd__whatrow">
          <input
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            placeholder={`예: ${spec.example}`}
            maxLength={200}
          />
          {/* 🎲 빈 칸이 가장 흔한 벽이다. 예시는 하나뿐이라 그것만 따라 하게 되는데,
              여기서 뽑으면 **이 책 세계 안의** 여러 갈래를 보게 된다(경주·신라·그림책).
              뽑아 놓고 고치는 게 정상 흐름이라 그대로 입력칸에 넣는다. */}
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setWhat(randomArtIdea(kind, Math.random, what.trim() || undefined))}
            title="이 책에 어울리는 주제를 하나 골라 넣어요. 넣고 고치셔도 돼요"
          >
            🎲 아무거나
          </button>
        </span>
      </label>
      )}

      <div className="artd__acts">
        <ActionButton
          className="btn btn--primary btn--sm"
          disabled={!said}
          onAction={make}
          doneLabel="후보가 나왔어요 — 마음에 드는 것을 눌러 주세요"
        >
          ✨ 여기서 바로 만들기
        </ActionButton>
        {/* 구독으로 만드는 길 — 손이 가장 많이 가는 「프롬프트 쓰기」를 대신해 준다 */}
        <ActionButton
          className="btn btn--sm"
          disabled={!said}
          onAction={async () => {
            await navigator.clipboard.writeText(`${shown} ${parts.fixed}`);
          }}
          doneLabel="복사했어요 — ChatGPT·제미나이·그록에 붙여넣으세요"
        >
          📋 프롬프트 복사
        </ActionButton>
        {left !== null ? <span className="hint">오늘 {left}장 남음</span> : null}
      </div>

      {/* 🖼️ 「이 그림처럼」 — 골라 두면 결이 훨씬 붙는다. 기본은 꺼져 있다(참고를 보내면
          그림 값이 조금 더 든다). 고른 것만 보낸다. */}
      {refs.length > 0 ? (
        <div className="artd__refs">
          <p className="artd__refs-h">
            🖼️ <b>이 그림처럼</b> 그려 주세요 <small>(고르면 결이 비슷해져요 · 최대 {ART_REFS_MAX}장)</small>
          </p>
          <div className="artd__refrow">
            {refs.map((r) => {
              const on = picked.includes(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  className="artd__ref"
                  data-on={on || undefined}
                  aria-pressed={on}
                  title={r.label}
                  onClick={() =>
                    setPicked((p) =>
                      on ? p.filter((k) => k !== r.key) : p.length >= ART_REFS_MAX ? p : [...p, r.key],
                    )
                  }
                >
                  <img src={r.url} alt={r.label} loading="lazy" />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* 📝 실제로 보내는 글 — 보고 고칠 수 있다. 접어 두는 이유: 대부분은 위 한 줄이면 되고,
          영어 문장이 늘 펼쳐져 있으면 화면이 어려워 보인다. */}
      <details className="artd__prompt">
        <summary>📝 AI에게 보내는 글 보기·고치기{touched ? " (고쳤어요)" : ""}</summary>
        <textarea
          className="artd__ta"
          value={shown}
          maxLength={ART_PROMPT_MAX}
          rows={5}
          onChange={(e) => setEdited(e.target.value)}
          spellCheck={false}
        />
        <p className="artd__fixed">
          <b>여기까지가 고칠 수 있는 부분이고, 아래는 늘 붙어요</b> — 지워지면 흰 배경이 붙거나
          아이에게 맞지 않는 그림이 나와요.
          <br />
          <code>{parts.fixed}</code>
        </p>
        {touched ? (
          <button type="button" className="btn btn--sm" onClick={() => setEdited(null)}>
            ↩ 기본 글로 되돌리기
          </button>
        ) : null}
      </details>

      {/* 🔴 「여기서 바로」가 안 되는 날에도 길이 있어야 한다 — 쓰시는 AI(구독)로 만든 그림 넣기.
          <label> + 숨긴 <input type=file> 이라 **누르면 파일 창이 열리고** 키보드로도 닿는다
          (직접 만든 클릭 핸들러는 탭으로 못 가는 자리가 되기 쉽다). */}
      <label
        className={`artd__drop${over ? " artd__drop--over" : ""}`}
        onPaste={onPaste}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          takeFile(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            takeFile(e.target.files?.[0]);
            e.target.value = ""; // 같은 파일을 다시 골라도 다시 들어오게
          }}
        />
        <b>🖼️ 그림 파일 고르기</b>
        <span>여기에 끌어다 놓아도 되고, Ctrl+V 로 붙여넣어도 돼요</span>
        <small>ChatGPT·제미나이·그록 같은 데서 만든 그림을 그대로 넣으세요</small>
      </label>
      {note ? <p className="hint">{note}</p> : null}

      {images.length > 0 ? (
        <>
          <p className="hint">
            [✅ 이 그림 쓰기]를 누르면 아래 칸에 들어가요. 아이 화면에 나가려면 그다음
            <b> [🚀 웹에 올리기]</b>까지 눌러 주세요. 마음에 안 들면 다시 만들어도 돼요.
          </p>
          <div className="artd__grid">
            {images.map((src, i) => (
              <figure key={i} className="artd__cand">
                <img src={src} alt={`후보 ${i + 1}`} />
                {/* 🔴 그림을 누르면 되는 줄 몰라서 한참 헤맬 수 있다 — **버튼으로 적어 준다.**
                    「쓰기」는 아래 칸에 넣는 것이고, 아이 화면에 나가려면 [🚀 웹에 올리기]가 남아 있다. */}
                <figcaption className="artd__pick">
                  <ActionButton
                    className="btn btn--primary btn--sm"
                    onAction={async () => onPick(await toFile(src, i))}
                    doneLabel="아래 칸에 넣었어요"
                  >
                    ✅ 이 그림 쓰기
                  </ActionButton>
                  <ActionButton
                    className="btn btn--sm"
                    onAction={() => download(src, i)}
                    doneLabel="저장했어요"
                    title="내 컴퓨터에 그림 파일로 저장해요"
                  >
                    ⬇
                  </ActionButton>
                </figcaption>
              </figure>
            ))}
          </div>
        </>
      ) : null}

      <p className="hint">
        AI 그림은 <b>초안</b>이에요. 이 책은 연필·수채 그림이라 한 번에 결이 안 맞을 수 있어요 —
        마음에 들 때까지 다시 만들거나, 쓰시는 AI에서 만들어 붙여넣으셔도 돼요.
      </p>
    </details>
  );
}
